import { TileType } from '../types/map.types';

/**
 * Convention: the map uses y+1 = up (math). Rendering negates y, so lower
 * on screen ↔ smaller y in the map ↔ in front of the camera (2.5D).
 * At equal Y, the order is: environment < location < groundItem < entity.
 * Overlays sit above everything via a high Y-independent constant.
 */

export const Y_MULTIPLIER = 10;

export const PRIORITY = {
  environment: 0,
  location: 1,
  groundItem: 2,
  entity: 3,
} as const;

/** zIndex for overlays always above the game (spells, aimer). */
export const OVERLAY_Z_INDEX = 9000;

/** zIndex for HUD/debug that must sit in front of overlays. */
export const DEBUG_Z_INDEX = 9999;

export type ZIndexOptions =
  | {
      type: 'environment';
      posY: number;
      /** Tile sub-type, lets ordering be tuned per category. */
      envType?: TileType;
    }
  | {
      type: 'location';
      /** Reference y of the PlacedLocation (topmost row of the footprint). */
      posY: number;
      /** Footprint height in tiles. Default: 1. */
      height?: number;
    }
  | {
      type: 'groundItem';
      posY: number;
    }
  | {
      type: 'entity';
      posY: number;
    }
  | {
      type: 'overlay';
    }
  | {
      /** Affected-area preview for an attack/spell. Below other overlays. */
      type: 'attackPreview';
    }
  | {
      type: 'debug';
    };

/**
 * `posY` is expected in tile units (not pixels).
 */
export function getZIndex(opts: ZIndexOptions): number {
  switch (opts.type) {
    case 'environment':
      // 'ground' tiles are always pushed to the very back: pure floor (no
      // relief) must never pass in front of walls or entities even when its
      // Y row sits lower on screen.
      if (opts.envType === 'ground') {
        return -opts.posY * Y_MULTIPLIER + PRIORITY.environment - 10000;
      }
      return -opts.posY * Y_MULTIPLIER + PRIORITY.environment;
    case 'location': {
      const height = opts.height ?? 1;
      // PlacedLocation convention: posY = top row, the footprint extends down
      // to `posY - height + 1`. Sort by that bottom row so the multi-tile
      // sprite passes in front of every environment tile in its footprint.
      const bottomY = opts.posY - (height - 1);
      return -bottomY * Y_MULTIPLIER + PRIORITY.location;
    }
    case 'groundItem':
      return -opts.posY * Y_MULTIPLIER + PRIORITY.groundItem;
    case 'entity':
      return -opts.posY * Y_MULTIPLIER + PRIORITY.entity;
    case 'overlay':
      return OVERLAY_Z_INDEX;
    case 'attackPreview':
      return -9000;
    case 'debug':
      return DEBUG_Z_INDEX;
  }
}
